import { Component } from '@theme/component';
import { mediaQueryLarge } from '@theme/utilities';
import {
  ThemeEvents,
  VariantUpdateEvent,
  ZoomMediaSelectedEvent,
  EngravingGalleryPromoteEvent,
} from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {HTMLElement | null} */
  #engravingRestoreAnchor = null;

  /** @type {boolean} */
  #engravingPromoted = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.engravingGalleryPromote, this.#handleEngravingGalleryPromote, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener('error', this.#handleBrokenGalleryImage, { capture: true, signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    const wasPromoted = this.#engravingPromoted;
    this.replaceWith(newMediaGallery);

    if (wasPromoted && newMediaGallery instanceof MediaGallery) {
      queueMicrotask(() => {
        newMediaGallery.#setEngravingPromoted(true);
      });
    }
  };

  /**
   * @param {Event} event
   */
  #handleEngravingGalleryPromote = (event) => {
    if (!(event instanceof EngravingGalleryPromoteEvent)) return;
    this.#setEngravingPromoted(event.detail.active);
  };

  /**
   * @returns {HTMLElement | null}
   */
  #getGridEngravingElement() {
    return this.querySelector('.media-gallery__grid [data-media-role="engraving"]');
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getSlideshowEngravingElement() {
    return this.querySelector('slideshow-slide[data-media-role="engraving"]');
  }

  /**
   * @param {boolean} active
   */
  #setEngravingPromoted(active) {
    const gridEngraving = this.#getGridEngravingElement();
    const slideEngraving = this.#getSlideshowEngravingElement();
    if (!gridEngraving && !slideEngraving) return;

    if (active) {
      this.#promoteEngraving(gridEngraving, slideEngraving);
    } else {
      this.#restoreEngraving(gridEngraving, slideEngraving);
    }
  }

  /**
   * @param {HTMLElement | null} gridEngraving
   * @param {HTMLElement | null} slideEngraving
   */
  #promoteEngraving(gridEngraving, slideEngraving) {
    if (this.#engravingPromoted) return;

    const grid = this.querySelector('.media-gallery__grid');
    if (gridEngraving && grid && mediaQueryLarge.matches) {
      this.#engravingRestoreAnchor = gridEngraving.nextElementSibling;
      grid.insertBefore(gridEngraving, grid.firstElementChild);
    }

    const slideIndex = this.#getEngravingSlideIndex(slideEngraving);
    const useSlideshow =
      slideIndex >= 0 && (!mediaQueryLarge.matches || this.presentation === 'carousel' || !gridEngraving);
    if (useSlideshow) {
      this.slideshow?.select(slideIndex, undefined, { animate: false });
    }

    this.classList.add('media-gallery--engraving-promoted');
    this.#engravingPromoted = true;
  }

  /**
   * @param {HTMLElement | null} gridEngraving
   * @param {HTMLElement | null} slideEngraving
   */
  #restoreEngraving(gridEngraving, slideEngraving) {
    if (!this.#engravingPromoted) return;

    const grid = this.querySelector('.media-gallery__grid');
    if (gridEngraving && grid) {
      grid.insertBefore(gridEngraving, this.#engravingRestoreAnchor);
    }

    this.#engravingRestoreAnchor = null;

    if (slideEngraving) {
      this.slideshow?.select(0, undefined, { animate: false });
    }

    this.classList.remove('media-gallery--engraving-promoted');
    this.#engravingPromoted = false;
  }

  /**
   * @param {HTMLElement | null} slideEngraving
   * @returns {number}
   */
  #getEngravingSlideIndex(slideEngraving) {
    if (!slideEngraving || !this.slideshow) return -1;

    const slides = this.slideshow.refs?.slides;
    if (!slides?.length) return -1;

    return slides.indexOf(slideEngraving);
  }

  /**
   * Hides the gallery slide/grid cell when a product image fails to load (bad URL, 404, etc.).
   * @param {Event} event
   */
  #handleBrokenGalleryImage = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLImageElement) || !target.classList.contains('product-media__image')) return;

    const host = target.closest('slideshow-slide, li.product-media-container');
    if (!host) return;

    host.setAttribute('hidden', '');
    host.setAttribute('aria-hidden', 'true');
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    const offset = parseInt(this.dataset.zoomSlideshowOffset || '0', 10) || 0;
    const slideshowIndex = event.detail.index + offset;
    this.slideshow?.select(slideshowIndex, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
